// components/MapTypeSelector.tsx
import React from "react";
import type { MapEnginePlugin, MapType } from "./types/MapTypes";

interface MapTypeSelectorProps {
  current: MapType;
  engines: MapEnginePlugin[];
  onChange: (type: MapType) => void;
}

const MapTypeSelector: React.FC<MapTypeSelectorProps> = ({ current, engines, onChange }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "12px",
        right: "12px",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        background: "rgba(15, 17, 26, 0.92)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "12px",
        padding: "8px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontWeight: 700,
          letterSpacing: "2px",
          color: "rgba(255,255,255,0.35)",
          textTransform: "uppercase",
          textAlign: "center",
          padding: "2px 4px 6px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          marginBottom: "2px",
        }}
      >
        Engine
      </div>
      {engines.map((engine) => {
        const isActive = current === engine.type;
        return (
          <button
            key={engine.type}
            onClick={() => onChange(engine.type)}
            title={engine.description}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              background: isActive ? "linear-gradient(135deg, #00d4ff22, #0066ff22)" : "transparent",
              border: isActive ? "1px solid #00d4ff55" : "1px solid transparent",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              color: isActive ? "#00d4ff" : "rgba(255,255,255,0.55)",
              fontSize: "11px",
              fontWeight: isActive ? 700 : 400,
              letterSpacing: "0.5px",
              textAlign: "left",
              width: "100%",
              outline: "none",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.color = "rgba(255,255,255,0.85)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "rgba(255,255,255,0.55)";
              }
            }}
          >
            <span style={{ fontSize: "16px", lineHeight: 1 }}>{engine.icon}</span>
            <span>{engine.label}</span>
            {engine.supports3D && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "8px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: isActive ? "#00d4ff" : "rgba(255,165,0,0.6)",
                  background: isActive ? "rgba(0,212,255,0.15)" : "rgba(255,165,0,0.1)",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  border: `1px solid ${isActive ? "rgba(0,212,255,0.3)" : "rgba(255,165,0,0.2)"}`,
                }}
              >
                3D
              </span>
            )}
            {isActive && (
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "2px",
                  background: "#00d4ff",
                  borderRadius: "0 2px 2px 0",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default MapTypeSelector;
